/* eslint-disable consistent-return */
/* eslint-disable no-restricted-syntax */
import React from 'react';
import Head from 'next/head';
import Link from 'next/link';

import { DEFAULT_REVALIDATION_TIME } from 'utils/shared';
import { getFallbackMostReadPosts, getHomePageData, processBlogHomeCategories } from 'lib/wordpress';
import PostCardsList from 'components/PostCardList';
import withLayoutHOF from 'components/withLayoutHOF';
import CategoryTitle from 'components/CategoryTitle';
import Button from 'components/Button';
import MostRead from 'components/MostRead';
import { addDefaultValues } from 'utils/seoHead';
import { parseImageUrl } from 'utils/imageUtils';
import { InferGetStaticPropsType } from 'next';
import validateProps from 'utils/props-validations/homepage';
import { processBlogHomeOptions } from 'utils/helpers';
import { client } from 'lib/apollo/client';
import processMostReadPosts from 'utils/mostReadHelpers';
import { MostReadFragment } from 'gql/graphql-operations';
import MainContainer from 'components/MainContainer';
import extractText from 'utilities/src/extractText';
import Hero, { HeroProps } from 'components/Hero';

export type HomeProps = InferGetStaticPropsType<typeof getStaticProps>;

export const blogPostListClasses = `w-full max-w-7xl mx-auto
  [&>a:first-of-type]:row-start-1 [&>a:last-of-type]:justify-start
  mdS:pb-16 mdS:gap-y-5 mdS:[&>a:last-of-type]:my-0 mdS:[&>a:last-of-type]:justify-end mdS:[&>a:last-of-type]:col-start-2 mdS:[&>a:last-of-type]:row-start-1
  mdS:[&>a:first-of-type]:col-span-2 mdS:[&>a:first-of-type]:col-start-1 mdS:[&>a:last-of-type]:col-span-1 mdS:[&>a:last-of-type]:justify-self-end mdS:[&>a:first-of-type]:self-center     
  mdSL:gap-x-3.5
  mdL:pb-8 mdL:[&>a:last-of-type]:col-start-3`;

export default function IndexPage({ title, featuredPost, categories, mostReadPosts }: HomeProps): JSX.Element {
  return (
    <div>
      <Head>
        <title>{extractText(title)}</title>
      </Head>
      <main className="pt-20">
        <h1 className="sr-only">Your blog for Outsourcing and Tech News</h1>
        <MainContainer>
          <div className="mdL:flex-row mx-auto box-content flex flex-col justify-between gap-8 pb-10">
            <Hero post={featuredPost as HeroProps['post']} />
            <MostRead posts={mostReadPosts} />
          </div>
        </MainContainer>
        <div className="bg-site-neutrals-25">
          <MainContainer>
            <section className="mdSL:pt-14 mdL:pt-20 pb-24 pt-9">
              {categories?.map(({ category, posts }) => (
                <PostCardsList
                  key={category?.id ?? ''}
                  componentsAfterCards={
                    posts?.length && <CategoryTitle category={category?.name ?? ''} slug={category?.slug ?? ''} />
                  }
                  posts={posts ?? []}
                  classes={blogPostListClasses}
                />
              ))}
              <Link href="/page/1" className="w-full">
                <Button
                  type="button"
                  variant="Primary"
                  variantColor="Black"
                  size="Medium"
                  iconName="ArrowForward"
                  classes="mx-auto"
                >
                  See all articles
                </Button>
              </Link>
            </section>
          </MainContainer>
        </div>
      </main>
    </div>
  );
}

export const HOME_REVALIDATION_TIME = DEFAULT_REVALIDATION_TIME;

export const getStaticProps = withLayoutHOF(async () => {
  if (!client) throw new Error(`Apollo Client is not defined`);

  const excludeIds: string[] = [];

  const { data, errors } = await getHomePageData();
  const mostReadFallbackFn = () =>
    getFallbackMostReadPosts(data.acfOptionsBlogHomepageOptions?.blogHomeOptions?.featuredPost?.id);

  const mostReadPosts = (await processMostReadPosts(data, mostReadFallbackFn)) as MostReadFragment[];

  const { title, featuredPost, categories } = processBlogHomeOptions(data, mostReadPosts);

  const blogTitle =
    title?.replaceAll('&amp;', '&') ?? 'BairesDev Blog: Insights on Software Development & Tech Talent';

  // Make sure the ID of the featured post is excluded from later queries
  excludeIds.push(featuredPost!.id);

  // do the same for most read
  mostReadPosts?.forEach((post) => excludeIds.push(post!.id));

  const homepageCategories = await processBlogHomeCategories(categories, excludeIds);

  if (errors) {
    throw new Error(`GET_HOMEPAGE_DATA: ${JSON.stringify(errors)}`);
  }

  const image =
    data?.rankMathSettings?.meta?.homepage?.socialImage ??
    data?.acfOptionsBlogHomepageOptions?.blogHomeOptions?.featuredPost?.featuredImage?.node;

  const seoData = data?.nodeByUri?.__typename === 'ContentType' ? data.nodeByUri.seo : null;

  const robots =
    process.env?.NEXT_PUBLIC_ENABLE_ROBOTS === 'true'
      ? data?.acfOptionsBlogHomepageOptions?.blogHomeOptions?.featuredPost?.seo?.robots
      : ['nofollow', 'noindex', 'noarchive', 'noimageindex', 'nosnippet'];

  const seo = {
    ...seoData,
    // Temporary until fix description field
    description: data?.rankMathSettings?.meta?.homepage?.socialDescription,
    robots,
    openGraph: {
      ...seoData?.openGraph,
      image: {
        height: image?.mediaDetails?.height,
        width: image?.mediaDetails?.width,
        secureUrl: parseImageUrl(image?.sourceUrl ?? ''),
        url: parseImageUrl(image?.sourceUrl ?? ''),
        type: image?.mimeType,
      },
      description: data?.rankMathSettings?.meta?.homepage?.socialDescription?.replaceAll('&amp;', '&'),
      title: data?.rankMathSettings?.meta?.homepage?.socialTitle?.replaceAll('&amp;', '&'),
      siteName: seoData?.openGraph?.siteName?.replaceAll('&amp;', '&'),
    },
    ogImageAlt: image?.altText,
  };

  const props = {
    featuredPost,
    categories: homepageCategories.filter((cat): cat is NonNullable<typeof cat> => !!cat),
    mostReadPosts,
    title: blogTitle,
    seo: addDefaultValues(seo, blogTitle),
  };

  validateProps(props);

  return {
    props,
    // revalidate: HOME_REVALIDATION_TIME, // ISR
  };
}, 'Homepage');
